// src/shared/utils/userUtils.ts
import { User as EmployeeInterface } from "src/shared/services/interfacev2";

/**
 * Safely gets the ID from a user object regardless of its structure
 */
export const getUserId = (user: any): string => {
  if (!user) return "";
  if (user._id?.$oid) return user._id.$oid;
  if (user.id) return String(user.id);
  if (user._id && typeof user._id === "string") return user._id;
  return "";
};

/**
 * Enriches user data to ensure it has the proper structure
 * with _id, phones, emails, and roles arrays
 */
export const enrichUserData = (user: any): EmployeeInterface => {
  const enriched: any = { ...user };

  // Ensure _id exists
  if (!enriched._id) {
    enriched._id = { $oid: user.id || Date.now().toString() };
  }

  // ✅ Handle phones array - preserve it
  if (user.phones && Array.isArray(user.phones) && user.phones.length > 0) {
    enriched.phones = user.phones
      .map((phone: any) => {
        if (typeof phone === "object" && phone !== null) {
          return {
            phone: phone.phone || phone.number || "",
            isPrimary: phone.isPrimary || false,
          };
        }
        if (typeof phone === "string") {
          return { phone: phone, isPrimary: false };
        }
        return { phone: "", isPrimary: false };
      })
      .filter((p: any) => p.phone);
  } else {
    // Fallback: try to get phone from other fields
    const phoneNumber = user.phoneNumber || user.phone || user.phone_number;
    if (phoneNumber && phoneNumber !== "N/A" && phoneNumber !== "null" && phoneNumber !== "undefined") {
      enriched.phones = [{ phone: String(phoneNumber), isPrimary: true }];
    } else {
      enriched.phones = [];
    }
  }

  // ✅ Handle emails array - preserve it
  if (user.emails && Array.isArray(user.emails) && user.emails.length > 0) {
    enriched.emails = user.emails
      .map((email: any) => {
        if (typeof email === "object" && email !== null) {
          return {
            email: email.email || "",
            isPrimary: email.isPrimary || false,
          };
        }
        if (typeof email === "string") {
          return { email: email, isPrimary: false };
        }
        return { email: "", isPrimary: false };
      })
      .filter((e: any) => e.email);
  } else {
    // Fallback: try to get email from other fields
    const emailValue = user.email || user.email_id;
    if (emailValue && emailValue !== "N/A" && emailValue !== "null" && emailValue !== "undefined") {
      enriched.emails = [{ email: String(emailValue), isPrimary: true }];
    } else {
      enriched.emails = [];
    }
  }

  // ✅ Handle roles - keep as array
  if (user.roles) {
    if (Array.isArray(user.roles)) {
      enriched.roles = user.roles;
    } else if (typeof user.roles === "string") {
      enriched.roles = [user.roles];
    } else {
      enriched.roles = [];
    }
  } else {
    enriched.roles = [];
  }

  return enriched as EmployeeInterface;
};

/**
 * Enriches an array of users with proper structure
 */
export const enrichUsersData = (users: any[]): EmployeeInterface[] => {
  if (!users || !Array.isArray(users)) return [];
  return users.map((user) => enrichUserData(user));
};

/**
 * Extracts a clean phone number from a user object
 */
export const extractPhoneNumber = (user: any): string => {
  if (!user) return "N/A";

  // Check phones array
  if (user.phones && Array.isArray(user.phones) && user.phones.length > 0) {
    const firstPhone = user.phones[0];
    if (typeof firstPhone === "object" && firstPhone !== null && "phone" in firstPhone) {
      const phone = firstPhone.phone;
      if (phone && phone !== "N/A" && phone !== "null" && phone !== "undefined") {
        return phone;
      }
    }
    if (typeof firstPhone === "string") {
      return firstPhone;
    }
  }

  // Check phoneNumber field
  if (user.phoneNumber && user.phoneNumber !== "N/A" && user.phoneNumber !== "null" && user.phoneNumber !== "undefined") {
    return user.phoneNumber;
  }

  // Check phone field
  if (user.phone && user.phone !== "N/A" && user.phone !== "null" && user.phone !== "undefined") {
    return user.phone;
  }

  // Check phone_number field
  if (user.phone_number && user.phone_number !== "N/A" && user.phone_number !== "null" && user.phone_number !== "undefined") {
    return user.phone_number;
  }

  return "N/A";
};

/**
 * Extracts a clean email from a user object
 */
export const extractEmail = (user: any): string => {
  if (!user) return "N/A";

  // Check emails array
  if (user.emails && Array.isArray(user.emails) && user.emails.length > 0) {
    const firstEmail = user.emails[0];
    if (typeof firstEmail === "object" && firstEmail !== null && "email" in firstEmail) {
      const email = firstEmail.email;
      if (email && email !== "N/A" && email !== "null" && email !== "undefined") {
        return email;
      }
    }
    if (typeof firstEmail === "string") {
      return firstEmail;
    }
  }

  // Check email field
  if (user.email && user.email !== "N/A" && user.email !== "null" && user.email !== "undefined") {
    return user.email;
  }

  // Check email_id field
  if (user.email_id && user.email_id !== "N/A" && user.email_id !== "null" && user.email_id !== "undefined") {
    return user.email_id;
  }

  return "N/A";
};

/**
 * Gets the full name from a user object
 */
export const getUserFullName = (user: any): string => {
  if (!user) return "";
  const firstName = user.firstName || user.first_name || "";
  const middleName = user.middleName || user.middle_name || "";
  const lastName = user.lastName || user.last_name || "";

  return `${firstName} ${middleName} ${lastName}`.trim().replace(/\s+/g, " ");
};

/**
 * Checks if a user is deleted
 */
export const isUserDeleted = (user: any): boolean => {
  if (!user) return false;
  return Boolean(user.dateOfLeaving || user.deletedAt || user.isDeleted);
};

/**
 * Gets the user's status display text
 */
export const getUserStatusText = (user: any): string => {
  if (!user) return "N/A";

  if (user.dateOfLeaving || user.deletedAt || user.isDeleted === true) {
    return "🟥 Deleted";
  }

  const status = user.status?.toLowerCase() || "";

  if (status === "active") return "🟢 Active";
  if (status === "inactive") return "🟠 Inactive";

  return "N/A";
};
